import React, { useEffect, useMemo } from "react";
// NAVIGATION
import { Link, useLocation } from "react-router-dom";
// ANT DESIGN COMPONENTS
import { Breadcrumb } from "antd";
import { useSelector } from "react-redux";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// TYPES AND DATA
import type { MapMetaData } from "pages/mapViewManagement/mapSelector/types";
import formatUuidT from "../../../flatbuffers/display/uuid";
import GETJwtToken from "../../../redux/authentication/authentication";
import { useLazyGetMapViewQuery } from "../../../redux/mapView/mapViewAPI";
import { RootState } from "../../../app/store";

function MapViewBreadcrumb() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");
  const [triggerGetMapView, { data: dataGetMapView }] = useLazyGetMapViewQuery();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const location = useLocation();
  const { globalOrganizationId, userLogged } = useSelector((state: RootState) => state.home);
  const resolvedOrganizationId =
    globalOrganizationId || userLogged.organizationId || userLogged.org?.id || "";
  const editorRouteMatch = location.pathname.match(/\/edge-management\/map-view\/editor\/([^/]+)/);
  const editorMapId = editorRouteMatch?.[1] ?? "";

  useEffect(() => {
    const getInitialData = async () => {
      if (!resolvedOrganizationId) {
        return;
      }

      const token = await GETJwtToken();
      triggerGetMapView({
        token,
        body: {
          org_id: resolvedOrganizationId,
        },
      });
    };

    getInitialData();
  }, [resolvedOrganizationId, triggerGetMapView]);

  const mapRows: MapMetaData[] = useMemo(() => {
    const apiRows: any[] = Array.isArray(dataGetMapView?.data) ? dataGetMapView.data : [];

    return apiRows.map(
      (item: any) =>
        ({
          id: formatUuidT(item?.id),
          mapName: item?.name || item?.mapName || "",
          company: item?.company || "",
          mapImgUrl: item?.map_img_url || item?.mapImgUrl || item?.offline_url || "",
          waypoints: item?.waypoints || [],
          groups: item?.group_waypoints || item?.groups || [],
          banks: item?.bank_waypoints || item?.banks || [],
        }) as MapMetaData,
    );
  }, [dataGetMapView]);

  const editorMap = mapRows.find((item) => item.id === editorMapId);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Breadcrumb>
      <Breadcrumb.Item>{t("header.edgeManagement")}</Breadcrumb.Item>
      <Breadcrumb.Item className="generalStyles__breadcrumbItem">
        <Link to="/edge-management/map-view" style={{ fontWeight: 700 }}>
          {t("general.mapViewTable")}
        </Link>
      </Breadcrumb.Item>
      {/\/edge-management\/map-view\/configuration$/.test(location.pathname) && (
        <Breadcrumb.Item className="generalStyles__breadcrumbItem">
          {t("general.mapViewConfig")}
        </Breadcrumb.Item>
      )}
      {/\/edge-management\/map-view\/editor\//.test(location.pathname) && (
        <Breadcrumb.Item className="generalStyles__breadcrumbItem">
          {editorMap?.mapName || editorMapId}
        </Breadcrumb.Item>
      )}
    </Breadcrumb>
  );
}

export default MapViewBreadcrumb;
